
import React from "react";
import { DialogStateContextType } from "./DialogStateTypes";
import { useDialogStateCore } from "./hooks/useDialogStateCore";
import { useDialogStateLifecycle } from "./hooks/useDialogStateLifecycle";
import { DialogStateHookProps, DialogStateLifecycleProps } from "./hooks/DialogStateInternalTypes";

/**
 * Custom hook that manages all dialog state logic
 */
export function useDialogStateManager({
  open,
  onOpenChange,
  isSubmitting,
  forceKeepOpen: externalForceKeepOpen,
  installationPhase = false,
}: DialogStateHookProps): DialogStateContextType {
  // Use the core state hook to manage internal state
  const coreState = useDialogStateCore({
    open,
    onOpenChange,
    isSubmitting,
    forceKeepOpen: externalForceKeepOpen,
    installationPhase,
  });
  
  // Use the lifecycle hook to manage side effects
  const lifecycleProps: DialogStateLifecycleProps = {
    open,
    internalOpen: coreState.internalOpen,
    setInternalOpen: coreState.setInternalOpen,
    isSubmitting,
    externalForceKeepOpen,
    internalForceKeepOpen: coreState.forceKeepOpen,
    setInternalForceKeepOpen: coreState.setForceKeepOpen,
    installationPhase,
    isExplicitlyClosed: coreState.isExplicitlyClosed,
    onOpenChange,
    contentMountedRef: coreState.contentMountedRef,
    prevOpenRef: coreState.prevOpenRef,
    openStateChangeTimeoutRef: coreState.openStateChangeTimeoutRef,
    lastStateChangeTimestampRef: coreState.lastStateChangeTimestampRef,
    isInitialMountRef: coreState.isInitialMountRef,
    installedRef: coreState.installedRef
  };
  
  useDialogStateLifecycle(lifecycleProps);

  // Return only the public interface from DialogStateContextType
  return {
    internalOpen: coreState.internalOpen,
    setInternalOpen: coreState.setInternalOpen,
    isSubmitting,
    forceKeepOpen: coreState.forceKeepOpen,
    setForceKeepOpen: coreState.setForceKeepOpen,
    installationPhase,
    contentMounted: coreState.contentMounted,
    shouldBlockClose: () => coreState.shouldBlockClose, // Changed from boolean to function returning boolean
    handleOpenChange: coreState.handleOpenChange,
    resetExplicitlyClosed: coreState.resetExplicitlyClosed,
    isExplicitlyClosed: coreState.isExplicitlyClosed,
    setExplicitlyClosed: coreState.setExplicitlyClosed
  };
}
